/**
 * アンカー要素の直下に開くポップオーバー (動画エディタの Popover.tsx、シナリオのナレーター
 * VoiceSelect など) の表示位置の計算。
 * コンポーネントから切り離してあるのは、はみ出し時の寄せ方を単体テストで固定するため
 * (Fast Refresh の都合でコンポーネントのファイルに関数を同居させられない事情もある)。
 */
const VIEWPORT_MARGIN_PX = 8;
const ANCHOR_GAP_PX = 4;

export interface PopoverRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * ポップオーバーの表示位置を決める (副作用なし・単体テスト用に切り出し)。
 *
 * 横: align='left' はアンカーの左端に左端を、'right' は右端に右端を合わせる。どちらの場合も
 *     ビューポートからはみ出すときは端で止める (右にはみ出す → 右端に寄せる → それでも左に
 *     はみ出すなら左端。メニューが画面より広いときは左端が優先される)。
 * 縦: 原則アンカーの下。下に入りきらなければ上に出し、それでも入らなければ上端で止める。
 *     placement='above' のときは逆に原則アンカーの上に出し、上に入りきらなければ下に出す
 *     (モーダルのフッタなど、下に開くと画面や枠の外へ出てしまう場所のドロップダウン用)。
 */
export function computePopoverPosition(params: {
  anchor: PopoverRect;
  size: { width: number; height: number };
  viewport: { width: number; height: number };
  align: 'left' | 'right';
  placement?: 'below' | 'above';
}): { top: number; left: number } {
  const { anchor, size, viewport, align, placement = 'below' } = params;
  let left = align === 'right' ? anchor.right - size.width : anchor.left;
  if (left + size.width > viewport.width - VIEWPORT_MARGIN_PX) {
    left = viewport.width - VIEWPORT_MARGIN_PX - size.width;
  }
  left = Math.max(VIEWPORT_MARGIN_PX, left);

  const above = anchor.top - ANCHOR_GAP_PX - size.height;
  if (placement === 'above' && above >= VIEWPORT_MARGIN_PX) return { top: above, left };
  let top = anchor.bottom + ANCHOR_GAP_PX;
  if (placement === 'below' && top + size.height > viewport.height - VIEWPORT_MARGIN_PX) {
    top = Math.max(VIEWPORT_MARGIN_PX, anchor.top - ANCHOR_GAP_PX - size.height);
  }
  return { top, left };
}
